// Señales de INTENSIDAD de uso del operador (Item B2-emisión) + contador de core-actions
// per-usuario que consume el feedback-por-uso del preview (Item E#3) + espejo D1-lite al
// outbox del rollout (métrica `core_action_completion` de los experimentos `capability_launch`).
//
// Tres responsabilidades, una sola superficie para no duplicar el concepto de "acción core":
//   1. B2 — emitir a GA4 (reusando el sink de analytics) cada acción core de una feature con
//      `core_action=true` + `feature_key` + `by_user_id`, y un heartbeat de minutos activos.
//      Permite al dashboard identificar al "heavy daily operator" y ponderar después el
//      feedback por intensidad, sliceando por feature.
//   2. E#3 — llevar un contador per-usuario-por-feature (per-tab, en memoria) de core-actions
//      para disparar el prompt de feedback OMITIBLE tras N acciones reales. NO se persiste como
//      variante: es una señal de uso, no estado de rollout.
//   3. D1-lite — encolar `core_action_completed` al outbox del rollout (mismo canal que
//      switch/opt_out/telemetría del hook) para que el backoffice tenga DATOS con los que
//      calcular `core_action_completion`, la métrica primaria de `capability_launch`. THROTTLED
//      a 1 evento por comercio+feature+día (ver `hasEmittedCoreActionToday`): GA4 sigue
//      recibiendo cada acción sin throttle (B2 no cambia), pero el outbox del rollout persiste en
//      Postgres (`rollout_behavior_event`, append-only) — emitir sin throttle inflaría esa tabla
//      con el volumen real de core-actions.
//
// GENÉRICO por feature: `recordCoreAction(featureKey, action)` es la entrada canónica; el
// contador va keyed por `${userId}:${featureKey}` para que DOS features del framework nunca
// mezclen conteos (mezclarlos corrompería el `core_action_count` del usage_feedback de cada
// una). `recordMenuCoreAction` queda como wrapper de compatibilidad para los call-sites de menú.
//
// SIN PII: solo `by_user_id` (el id de usuario que el resto de telemetría ya emite) + el tipo de
// acción. Nunca nombre, email ni contenido del producto. Si el sink está apagado (no-prod / sin
// tracking), `ga4Event` es no-op y nada rompe.

import { ga4Event } from '@utils/analytics/ga4';
import { getStoreSnapshot } from './store';
import { isPreviewActive } from './preview';
import { getActiveCommerceId } from './cache';
import { enqueueTelemetry } from './outbox';
import { ulid } from './ulid';
import { MENU_FEATURE_KEY, isEntitlementFeature, type FeatureKey } from './constants';

// Tipos de acción core del menú. Estables (viajan en el payload GA4, no se traducen).
export type MenuCoreActionType = 'product_create' | 'product_edit' | 'product_reorder';
// Alias histórico (cuando solo existía el menú). Preferir `MenuCoreActionType` en código nuevo.
export type CoreActionType = MenuCoreActionType;

// Tipos de acción core de impresión BLE (impresion-ble). Mismas reglas de estabilidad.
export type BleCoreActionType = 'printer_paired' | 'print_attempt' | 'print_success';

// ¿La versión NUEVA de la feature está en pantalla para este usuario? = variante treatment
// adoptada por el comercio O vista previa per-usuario activa. Solo en ese caso una core-action
// es "uso de la versión nueva": las acciones en la versión vieja (control sin preview) NO deben
// contar ni emitir la señal de intensidad (Seer 14512239/1 — evita inflar core_action_count con
// uso de la versión anterior, que corrompería la ponderación del feedback por intensidad y el
// slice por variante).
function isFeatureOnScreen(featureKey: FeatureKey): boolean {
  const variant = getStoreSnapshot(featureKey).entry.variant;
  return variant === 'treatment' || isPreviewActive(featureKey);
}

// ¿Debe emitirse `core_action_completed` al OUTBOX del rollout para esta core-action?
//
// El supuesto A/B original (versión nueva OFRECIDA en pantalla) NO cubre las features de
// entitlement (`capability_launch`, ej. scheduled-orders): ahí el acceso es `hasModule OR
// treatment` (ver useScheduledOrdersGate), así que un comercio que TIENE el módulo y usa la
// feature bajo `variant='control'` es un usuario REAL de la capacidad y su uso debe medirse.
// Con el gate treatment-only ese uso quedaba invisible (mismatch acceso vs. emit).
//
// Predicado: emitir cuando la versión nueva está en pantalla (treatment/preview, comportamiento
// A/B intacto) O cuando es una feature de entitlement y el comercio tiene el módulo materializado.
// Genérico por `isEntitlementFeature`: NO acoplado a scheduled-orders. Solo gobierna el espejo al
// outbox del rollout; GA4 (B2) y el contador E#3 conservan su gate treatment-only (miden uso de
// la versión ofrecida, no de la capacidad ya poseída).
export function shouldEmitCoreAction(featureKey: FeatureKey, opts?: { hasModule?: boolean }): boolean {
  if (isFeatureOnScreen(featureKey)) return true;
  return isEntitlementFeature(featureKey) && opts?.hasModule === true;
}

// Nombre de evento GA4 (snake_case, coherente con el resto del catálogo de rollout).
const GA4_CORE_ACTION_EVENT = 'rollout_core_action';
const GA4_HEARTBEAT_EVENT = 'rollout_active_heartbeat';

// Cadencia del heartbeat de minutos activos: una marca por minuto de actividad real (no por
// tiempo de pestaña abierta inactiva). El consumidor llama `pingActivity()` ante interacción;
// solo emitimos si pasó al menos un minuto desde el último latido.
const HEARTBEAT_INTERVAL_MS = 60_000;

function byUserId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('id_user') ?? '';
  } catch {
    return '';
  }
}

// ── Contador de core-actions per-usuario-por-feature (per-tab, en memoria) ──────────────
// Keyed por `${userId}:${featureKey}`: un cambio de usuario en la misma pestaña (multisucursal)
// no hereda el conteo del anterior, y dos features del framework no se cruzan conteos. No se
// persiste: el prompt de feedback es per-sesión-de-uso.
const coreActionCounts = new Map<string, number>();
type CountListener = (count: number) => void;
const countListeners = new Map<string, Set<CountListener>>();

function countKey(userId: string, featureKey: string): string {
  return `${userId}:${featureKey}`;
}

function listenersFor(featureKey: string): Set<CountListener> {
  let set = countListeners.get(featureKey);
  if (!set) {
    set = new Set();
    countListeners.set(featureKey, set);
  }
  return set;
}

export function getCoreActionCount(featureKey: FeatureKey): number {
  return coreActionCounts.get(countKey(byUserId(), featureKey)) ?? 0;
}

// Notifica PULL, no PUSH: no difundimos el valor del contador (que es del usuario que acaba de
// actuar). Cada listener vuelve a leer getCoreActionCount() para SU usuario activo, evitando que
// el listener del usuario B reciba el conteo del usuario A en una carrera (Seer 14512239/0).
function notifyCount(featureKey: FeatureKey): void {
  const current = getCoreActionCount(featureKey);
  listenersFor(featureKey).forEach((l) => l(current));
}

// Suscribe al contador de core-actions del usuario activo PARA una feature. Devuelve unsubscribe.
export function subscribeCoreActionCount(featureKey: FeatureKey, listener: CountListener): () => void {
  const set = listenersFor(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// ── Contador de USO POR CAPACIDAD (survey de adopción de capability_launch) ──────────────
// Independiente del contador E#3 (treatment-only, arriba). Este cuenta el uso de CUALQUIER
// usuario REAL de la capacidad — el mismo predicado que gobierna el espejo al outbox
// (`shouldEmitCoreAction`: versión nueva en pantalla O feature de entitlement con módulo). Motivo:
// las features de entitlement (p.ej. scheduled-orders) tienen a sus usuarios mayormente en
// `control` (módulo-holders), que por DISEÑO no incrementan el contador E#3; su encuesta de
// adopción por uso necesita, aun así, dispararse. NO reemplaza al contador E#3 (menú/BLE lo siguen
// usando): es aditivo y per-tab/en-memoria, igual que aquél. Keyed por `${userId}:${featureKey}`.
const capabilityUsageCounts = new Map<string, number>();
const capabilityUsageListeners = new Map<string, Set<CountListener>>();

function capabilityListenersFor(featureKey: string): Set<CountListener> {
  let set = capabilityUsageListeners.get(featureKey);
  if (!set) {
    set = new Set();
    capabilityUsageListeners.set(featureKey, set);
  }
  return set;
}

export function getCapabilityUsageCount(featureKey: FeatureKey): number {
  return capabilityUsageCounts.get(countKey(byUserId(), featureKey)) ?? 0;
}

// Notifica PULL (mismo criterio anti-carrera que notifyCount): cada listener re-lee el conteo de
// SU usuario activo, evitando heredar el del usuario que acaba de actuar.
function notifyCapabilityUsage(featureKey: FeatureKey): void {
  const current = getCapabilityUsageCount(featureKey);
  capabilityListenersFor(featureKey).forEach((l) => l(current));
}

export function subscribeCapabilityUsageCount(featureKey: FeatureKey, listener: CountListener): () => void {
  const set = capabilityListenersFor(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

function bumpCapabilityUsage(featureKey: FeatureKey): void {
  const key = countKey(byUserId(), featureKey);
  capabilityUsageCounts.set(key, (capabilityUsageCounts.get(key) ?? 0) + 1);
  notifyCapabilityUsage(featureKey);
}

// ── Throttle del espejo al outbox del rollout (D1-lite) ─────────────────────────────────
// Persistido en localStorage (sobrevive reload/cierre de pestaña, a diferencia del contador E#3
// en memoria) keyed por comercio+feature: `plick:rollout-core-action-emit:v1:<commerce>:<feature>`
// guarda el día (YYYY-MM-DD, UTC) del último emit. Un comercio con varios usuarios en la misma
// sucursal comparte el throttle (es por comercio+feature, no por usuario+feature): el volumen que
// se protege es el de `rollout_behavior_event`, no la atribución al operador individual.
const CORE_ACTION_EMIT_PREFIX = 'plick:rollout-core-action-emit:v1:';

function todayUtcKey(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

function coreActionEmitStorageKey(commerceId: string, featureKey: string): string {
  return `${CORE_ACTION_EMIT_PREFIX}${commerceId}:${featureKey}`;
}

// ¿Ya encolamos `core_action_completed` para este comercio+feature HOY? Degradación segura: sin
// `window` o si localStorage falla, tratamos como "ya emitido" (no re-emitir) — preferimos perder
// un día de dato a arriesgar un loop de escritura fallida.
function hasEmittedCoreActionToday(commerceId: string, featureKey: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(coreActionEmitStorageKey(commerceId, featureKey)) === todayUtcKey();
  } catch {
    return true;
  }
}

function markCoreActionEmittedToday(commerceId: string, featureKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(coreActionEmitStorageKey(commerceId, featureKey), todayUtcKey());
  } catch {
    // ignore quota / private-mode
  }
}

// Solo para tests: limpia las marcas de throttle del día actual.
export function __resetCoreActionEmitThrottleForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(CORE_ACTION_EMIT_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

// ── Heartbeat de minutos activos ───────────────────────────────────────────────────────
let lastHeartbeatAt = 0;

// Marca actividad. Emite un latido a GA4 a lo sumo una vez por minuto. Idempotente dentro de
// la ventana: llamarlo en ráfaga no infla el conteo de minutos activos.
export function pingActivity(): void {
  const now = Date.now();
  if (now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
  lastHeartbeatAt = now;
  ga4Event(GA4_HEARTBEAT_EVENT, { by_user_id: byUserId() });
}

// ── Registro de una core-action ─────────────────────────────────────────────────────────
// Lo llaman los call-sites de cada feature (hooks de menú, impresión BLE, lista de programados)
// cuando el operador ejecuta una acción de valor. El heartbeat de minutos activos SIEMPRE se
// marca (mide actividad del operador, no una versión concreta). La señal de intensidad a GA4 (B2)
// y el contador E#3 solo se disparan si la versión nueva está EN PANTALLA (treatment/preview):
// miden uso de la versión OFRECIDA, no de la anterior (Seer 14512239/1). El espejo al outbox del
// rollout (D1-lite) usa un predicado más amplio para las features de entitlement — ver
// `shouldEmitCoreAction`.
//
// `opts.hasModule`: lo pasa el call-site de una feature de entitlement (capability_launch) con el
// resultado de su gate de módulo, para que un comercio que YA tiene la capacidad se mida aunque el
// rollout lo resuelva en control. Opcional: los call-sites A/B (menú, BLE) no lo pasan.
export function recordCoreAction(featureKey: FeatureKey, action: string, opts?: { hasModule?: boolean }): void {
  // El heartbeat es agnóstico de versión: cualquier acción es "operador activo".
  pingActivity();

  const onScreen = isFeatureOnScreen(featureKey);

  // B2 (GA4) + E#3 (contador): SIN CAMBIOS — solo cuando la versión nueva está en pantalla. En
  // control puro no miden "uso de la versión ofrecida" (evita inflar core_action_count con uso de
  // la versión anterior, que corrompería la ponderación del feedback y el slice por variante).
  if (onScreen) {
    const userId = byUserId();

    // B2 — señal a GA4. El `rollout_variant` ya viaja como user_property global (applyFeatureVersionTags),
    // así el dashboard puede slicear por variante sin que lo repitamos aquí. `feature_key` permite
    // separar las core-actions de cada feature del framework en el mismo evento.
    ga4Event(GA4_CORE_ACTION_EVENT, { core_action: true, action, feature_key: featureKey, by_user_id: userId });

    // E#3 — contador per-usuario-por-feature (per-tab) que dispara el prompt de feedback por uso.
    const key = countKey(userId, featureKey);
    coreActionCounts.set(key, (coreActionCounts.get(key) ?? 0) + 1);
    notifyCount(featureKey);
  }

  // D1-lite — espejo al outbox del rollout, THROTTLED a 1/comercio+feature/día. Emite cuando la
  // versión nueva está en pantalla O cuando es una feature de entitlement con módulo (ver
  // `shouldEmitCoreAction`): así un módulo-holder que usa la capacidad bajo control TAMBIÉN se
  // mide. Sin comercio activo no hay sujeto al que atar el evento (mismo guard que el resto del
  // outbox).
  if (!shouldEmitCoreAction(featureKey, { hasModule: opts?.hasModule })) return;

  // Contador de uso por capacidad (survey de adopción): incrementa para CADA acción de un usuario
  // real de la capacidad (treatment/preview O entitlement con módulo), sin throttle — el throttle de
  // abajo solo gobierna el espejo append-only al outbox, no esta señal de UI en memoria.
  bumpCapabilityUsage(featureKey);

  const commerceId = getActiveCommerceId();
  if (commerceId && !hasEmittedCoreActionToday(commerceId, featureKey)) {
    markCoreActionEmittedToday(commerceId, featureKey);
    // experiment_id del snapshot vigente (si lo hay): igual que `emit` del hook (SPEC-6), atamos
    // el evento al experimento que lo generó en vez de dejar que el backend resuelva el activo al
    // drenar (que podría ya no ser el mismo si el outbox tardó en vaciarse). Para un módulo-holder
    // en control puede ser null: el backend lo atribuye entonces al experimento running de la
    // feature (record-telemetry), que es justo el de adopción del capability_launch.
    const experimentId = getStoreSnapshot(featureKey).entry.experiment_id;
    enqueueTelemetry(
      {
        event_id: ulid(),
        feature_key: featureKey,
        behavior: 'core_action_completed',
        ...(experimentId ? { experiment_id: experimentId } : {}),
        occurred_at: new Date().toISOString(),
        payload: { action },
      },
      commerceId,
    );
  }
}

// Wrapper de compatibilidad para los call-sites de menú (hooks compartidos V1/V2 en
// create/edit/reorder de producto). @deprecated en código nuevo: usar
// `recordCoreAction(MENU_FEATURE_KEY, action)` directo.
export function recordMenuCoreAction(action: MenuCoreActionType): void {
  recordCoreAction(MENU_FEATURE_KEY, action);
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetUsageSignalsForTests(): void {
  coreActionCounts.clear();
  countListeners.clear();
  capabilityUsageCounts.clear();
  capabilityUsageListeners.clear();
  lastHeartbeatAt = 0;
  __resetCoreActionEmitThrottleForTests();
}
